import React, { useState, useEffect } from "react"
import { useHistory } from "react-router-dom"
import styled from "styled-components"
import PageLayout from "../common/PageLayout"
import MasonryView from "../common/MasonryView"
import CommunityListView from "../community/CommunityListView"
import Empty from "../common/Empty"
import { db, logUserEvent } from "../common/firebase.utils"
import Tabs from "./Tabs"
import Profile from "./Profile"
import { ORDER, ORDER_BY } from "../common/constants"
import { useUserInfo } from "../hooks/useUserInfo"
import useIsMobile from "../hooks/useIsMobile"
import MembershipBar from "./MembershipBar"
import TemplateEditPopup from "../templates/TemplateEditPopup"

export const TABS = { COPIED: "copied", MY: "my", COMMUNITY: "community" }

function My() {
  const [templates, setTemplates] = useState()
  const [posts, setPosts] = useState()
  const [currentTab, setCurrentTab] = useState(TABS.COPIED)
  const [editingTemplate, setEditingTemplate] = useState(null)

  const history = useHistory()
  const { userId, membershipFrom, membershipType } = useUserInfo()
  const isMobile = useIsMobile()
  const isCommunityTab = currentTab === TABS.COMMUNITY

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" })
  }, [])

  useEffect(() => {
    if (templates) setTemplates()
    if (userId) {
      if (currentTab === TABS.COPIED) {
        loadCopiedTemplates()
        logUserEvent("view_copied_templates")
      } else if (currentTab === TABS.MY) {
        loadMyTemplates()
        logUserEvent("view_my_templates")
      } else {
        loadMyPosts()
        logUserEvent("view_my_posts")
      }
    }
  }, [currentTab, userId]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadCopiedTemplates = async () => {
    const snapshot = await db
      .collection("copies")
      .where("userid", "==", userId)
      .get()

    const filteredCopies = snapshot.docs.filter(copy => !copy.data().isHidden)

    const docCount = filteredCopies.length
    if (!docCount) {
      setTemplates([])
    } else {
      // Helper function for timestamp handling
      const getTime = timestamp => {
        if (!timestamp) return new Date(0) // Very old date (epoch)
        return timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
      }

      // Group by templateid, but handle edited copies separately
      const templateCopyMap = new Map() // For non-edited templates (deduplicated)
      const editedCopies = [] // For edited templates (keep all copies)

      filteredCopies.forEach(copyDoc => {
        const copyData = copyDoc.data()
        const { templateid, createdat, editedcontent } = copyData

        if (editedcontent) {
          // For edited copies, keep all of them separately
          editedCopies.push({
            templateid,
            createdat,
            editedcontent,
            isEdited: true,
            copyId: copyDoc.id
          })
        } else {
          // For non-edited copies, deduplicate by keeping most recent
          const existingCopy = templateCopyMap.get(templateid)

          if (!existingCopy) {
            templateCopyMap.set(templateid, {
              templateid,
              createdat,
              isEdited: false,
              copyId: copyDoc.id
            })
          } else {
            const existingTime = getTime(existingCopy.createdat)
            const currentTime = getTime(createdat)

            if (currentTime > existingTime) {
              templateCopyMap.set(templateid, {
                templateid,
                createdat,
                isEdited: false,
                copyId: copyDoc.id
              })
            }
          }
        }
      })

      // Combine and sort all copies by createdat DESC
      const allCopies = [
        ...Array.from(templateCopyMap.values()),
        ...editedCopies
      ].sort((a, b) => {
        const aTime = getTime(a.createdat)
        const bTime = getTime(b.createdat)
        return bTime - aTime
      })

      // Fetch templates and build final result with edited content handling
      const templatePromises = allCopies.map(async copy => {
        const { templateid, isEdited, editedcontent, copyId } = copy

        if (isEdited) {
          // For edited copies, use editedcontent and fetch template for other fields
          try {
            const template = await db
              .collection("templates")
              .doc(templateid)
              .get()
            if (template.exists) {
              const templateData = template.data()
              return {
                ...templateData,
                content: editedcontent, // Use edited content instead of original
                id: templateid,
                isEdited: true,
                copyId
              }
            }
          } catch (error) {
            console.log("Error getting edited template:", error)
          }
          return null
        } else {
          // For non-edited copies, use original template content
          try {
            const template = await db
              .collection("templates")
              .doc(templateid)
              .get()
            if (template.exists) {
              return {
                ...template.data(),
                id: templateid,
                isEdited: false,
                copyId
              }
            }
          } catch (error) {
            console.log("Error getting template:", error)
          }
          return null
        }
      })

      const templateResults = await Promise.all(templatePromises)
      const _templates = templateResults.filter(template => template !== null)
      setTemplates(_templates)
    }
  }

  const loadMyTemplates = async () => {
    const snapshot = await db
      .collection("templates")
      .where("writerid", "==", userId)
      .where("deletedat", "==", null)
      .orderBy(ORDER_BY.CREATED_AT, ORDER.DESC)
      .get()
    const docCount = snapshot.size
    if (!docCount) {
      setTemplates([])
    } else {
      const _templates = []
      snapshot.docs.forEach(template => {
        let data = template.data()
        _templates.push({
          ...data,
          id: template.id
        })
      })
      setTemplates(_templates)
    }
  }

  const loadMyPosts = async () => {
    const snapshot = await db
      .collection("posts")
      .where("writerid", "==", userId)
      .where("deletedat", "==", null)
      .orderBy(ORDER_BY.CREATED_AT, ORDER.DESC)
      .get()
    const docCount = snapshot.size
    if (!docCount) {
      setPosts([])
    } else {
      const _posts = []
      snapshot.docs.forEach(post => {
        let data = post.data()
        _posts.push({
          ...data,
          id: post.id
        })
      })
      setPosts(_posts)
    }
  }

  const onClickEditTemplate = template => {
    setEditingTemplate(template)
  }

  const onClickTab = ({ id }) => {
    setCurrentTab(id)
  }

  const onClickPost = post => {
    history.push(`/community/${post.category}/${post.id}`)
  }

  const EmptyView = () => {
    if (currentTab === TABS.COPIED) {
      return (
        <Empty
          text={`아직 복사한 템플릿이 없네요!\n템플릿의 복사 버튼을 눌러 편하게 이용하세요`}
          buttonText={"템플릿 둘러보기"}
          onClickButton={() => history.push("/templates")}
        />
      )
    }
    if (currentTab === TABS.MY) {
      return (
        <Empty
          text={`아직 작성한 템플릿이 없네요!\n유용한 템플릿이 있다면 공유 부탁드려요`}
          buttonText={"템플릿 등록할게요"}
          onClickButton={() => history.push("/write")}
        />
      )
    }
    return (
      <Empty
        text={`아직 작성한 커뮤니티 글이 없네요!\n커뮤니티에 참여해보세요:)`}
        buttonText={"글 쓰러 갈래요"}
        onClickButton={() => history.push("/community")}
      />
    )
  }

  return (
    <PageLayout>
      <Header>
        {isMobile && membershipFrom == null && (
          <MembershipBar
            userId={userId}
            membershipFrom={null}
            membershipType={membershipType}
          />
        )}
      </Header>
      <Body>
        <div className="profile-and-tabs">
          <Profile />
          <Tabs
            onClick={({ target }) => onClickTab(target)}
            currentTab={currentTab}
          />
        </div>
        <ListContainer>
          {((isCommunityTab && posts?.length === 0) ||
            templates?.length === 0) && <EmptyView />}
          {isCommunityTab ? (
            <CommunityListView
              isLoading={!posts}
              posts={posts}
              onClickPost={p => onClickPost(p)}
              hideWriter={true}
            />
          ) : (
            <MasonryView
              isLoading={!templates}
              templates={templates}
              onSelectTag={() => {}}
              defaultColumns={2}
              gap="16px"
              onClickEditTemplate={onClickEditTemplate}
            />
          )}
        </ListContainer>
      </Body>
      {editingTemplate && (
        <TemplateEditPopup
          template={editingTemplate}
          onClose={() => setEditingTemplate(null)}
        />
      )}
    </PageLayout>
  )
}

const Header = styled.div`
  height: auto;
  margin-top: var(--gnb-height);
  padding-top: 32px;
  background: #fcfdff;
  text-align: center;
  @media (max-width: 800px) {
    width: 100%;
    height: auto;
    margin-top: var(--gnb-height);
    border-bottom: none;
    padding-top: 0;
    padding-bottom: 0;
  }
`

const Body = styled.div`
  display: flex;
  justify-content: center;
  max-width: 1138px;
  min-height: 55vh;
  margin: 0 auto 37px;
  padding-left: 24px;
  padding-right: 24px;
  .profile-and-tabs {
    margin-right: 16px;
  }
  @media (max-width: 800px) {
    flex-direction: column;
    padding: 0;
    margin-top: 0;
    .profile-and-tabs {
      margin-right: 0;
    }
  }
`

const ListContainer = styled.div`
  width: 100%;
  max-width: 744px;
  @media (max-width: 800px) {
    margin-top: 12px;
  }
`

export default My
